import React, { useMemo } from 'react';
import { ChartConfig, KPICardConfig, DEFAULT_KPI_CONFIG } from '../../types';
import { TrendingUp, TrendingDown, Minus, Target, DollarSign, Percent, Hash, Users, ShoppingCart, Activity, Zap } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area } from 'recharts';

interface KPICardProps {
    config: ChartConfig;
    data: any[];
    onCardClick?: (groupValue: string | null) => void;
}

interface KPIData {
    label: string;
    value: number;
    comparison?: number;
    delta?: number;
    deltaPercent?: number;
    trendData?: { value: number }[];
    groupValue: string | null;
}

// Icon mapping
const ICONS: Record<string, React.ReactNode> = {
    target: <Target className="w-6 h-6" />,
    dollar: <DollarSign className="w-6 h-6" />,
    percent: <Percent className="w-6 h-6" />,
    hash: <Hash className="w-6 h-6" />,
    users: <Users className="w-6 h-6" />,
    cart: <ShoppingCart className="w-6 h-6" />,
    activity: <Activity className="w-6 h-6" />,
    zap: <Zap className="w-6 h-6" />,
};

// Format number based on config
const formatValue = (value: number, config: KPICardConfig): string => {
    const { valueFormat, currencySymbol, decimalPlaces } = config;

    if (valueFormat === 'compact') {
        if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(decimalPlaces)}B`;
        if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(decimalPlaces)}M`;
        if (Math.abs(value) >= 1e3) return `${(value / 1e3).toFixed(decimalPlaces)}K`;
        return value.toFixed(decimalPlaces);
    }

    if (valueFormat === 'currency') {
        return `${currencySymbol}${value.toLocaleString(undefined, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
    }

    if (valueFormat === 'percent') {
        return `${value.toFixed(decimalPlaces)}%`;
    }

    return value.toLocaleString(undefined, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });
};

// Card size styles
const CARD_SIZES = {
    small: { padding: 'p-3', valueSize: 'text-2xl', labelSize: 'text-xs', iconSize: 'w-5 h-5' },
    medium: { padding: 'p-4', valueSize: 'text-3xl', labelSize: 'text-sm', iconSize: 'w-6 h-6' },
    large: { padding: 'p-6', valueSize: 'text-4xl', labelSize: 'text-base', iconSize: 'w-8 h-8' },
};

// Single KPI Card component
const SingleKPICard: React.FC<{
    kpi: KPIData;
    kpiConfig: KPICardConfig;
    onClick?: () => void;
}> = ({ kpi, kpiConfig, onClick }) => {
    const sizeStyles = CARD_SIZES[kpiConfig.cardSize];
    const isPositive = (kpi.delta ?? 0) > 0;
    const isNegative = (kpi.delta ?? 0) < 0;
    const isNeutral = kpi.delta === undefined || kpi.delta === 0;

    // Determine if delta is "good" based on deltaMode
    const isGood = kpiConfig.deltaMode === 'higher-is-better' ? isPositive : isNegative;
    const isBad = kpiConfig.deltaMode === 'higher-is-better' ? isNegative : isPositive;

    const deltaColor = isGood ? 'text-emerald-400' : isBad ? 'text-red-400' : 'text-gray-400';
    const trendColor = isGood ? '#34d399' : isBad ? '#f87171' : '#f59e0b';

    return (
        <div
            onClick={onClick}
            className={`
                relative overflow-hidden rounded-xl border border-white/10 
                bg-gradient-to-br from-obsidian-900/90 to-obsidian-950/90
                backdrop-blur-xl shadow-lg
                hover:border-neon-gold/30 hover:shadow-[0_0_30px_rgba(245,158,11,0.1)]
                transition-all duration-300 ease-out
                ${onClick ? 'cursor-pointer' : ''}
                ${sizeStyles.padding}
            `}
        >
            {/* Ambient glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-neon-gold/5 via-transparent to-transparent pointer-events-none" />

            <div className={`relative flex flex-col ${kpiConfig.alignment === 'center' ? 'items-center text-center' : 'items-start text-left'}`}>
                {/* Label */}
                <div className={`${sizeStyles.labelSize} font-medium text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-2`}>
                    {kpiConfig.showIcon && (
                        <span className="text-neon-gold/60">
                            {ICONS[kpiConfig.iconName] || ICONS.target}
                        </span>
                    )}
                    <span>{kpi.label}</span>
                </div>

                {/* Main Value */}
                <div className={`${sizeStyles.valueSize} font-bold text-white tracking-tight mb-1`}>
                    {formatValue(kpi.value, kpiConfig)}
                </div>

                {/* Delta */}
                {kpiConfig.showDelta && kpi.comparison !== undefined && (
                    <div className={`flex items-center gap-1 ${deltaColor} text-sm font-medium`}>
                        {isPositive && <TrendingUp className="w-4 h-4" />}
                        {isNegative && <TrendingDown className="w-4 h-4" />}
                        {isNeutral && <Minus className="w-4 h-4" />}
                        <span>
                            {isPositive ? '+' : ''}{kpi.deltaPercent?.toFixed(1)}%
                        </span>
                        <span className="text-gray-500 text-xs ml-1">vs previous</span>
                    </div>
                )}

                {/* Sparkline */}
                {kpiConfig.showTrendline && kpi.trendData && kpi.trendData.length > 1 && (
                    <div className="w-full h-10 mt-3 -mx-1">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={kpi.trendData}>
                                <defs>
                                    <linearGradient id={`gradient-${kpi.groupValue || 'default'}`} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={trendColor} stopOpacity={0.3} />
                                        <stop offset="100%" stopColor={trendColor} stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <Area
                                    type="monotone"
                                    dataKey="value"
                                    stroke={trendColor}
                                    strokeWidth={2}
                                    fill={`url(#gradient-${kpi.groupValue || 'default'})`}
                                    dot={false}
                                    isAnimationActive={true}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};

// Main KPI Card component
export const KPICard: React.FC<KPICardProps> = ({ config, data, onCardClick }) => {
    const kpiConfig: KPICardConfig = { ...DEFAULT_KPI_CONFIG, ...config.advanced?.kpiCard };

    // Process data into KPI items
    const kpiItems = useMemo<KPIData[]>(() => {
        if (!data || data.length === 0) {
            return [];
        }

        const valueField = config.kpiValueField || config.yField || config.measures?.[0]?.field;
        const comparisonField = config.kpiComparisonField;
        const groupByField = config.kpiGroupByField;
        const timeField = config.kpiTimeField;

        if (!valueField) {
            return [];
        }

        // If groupBy is set, create one card per group
        if (groupByField) {
            const groups = new Map<string, any[]>();

            data.forEach(row => {
                const groupKey = String(row[groupByField] || 'Unknown');
                if (!groups.has(groupKey)) {
                    groups.set(groupKey, []);
                }
                groups.get(groupKey)!.push(row);
            });

            const items: KPIData[] = [];

            groups.forEach((groupData, groupKey) => {
                // Sum values in group
                const value = groupData.reduce((sum, row) => sum + (Number(row[valueField]) || 0), 0);
                const comparison = comparisonField
                    ? groupData.reduce((sum, row) => sum + (Number(row[comparisonField]) || 0), 0)
                    : undefined;

                const delta = comparison !== undefined ? value - comparison : undefined;
                const deltaPercent = comparison && comparison !== 0 ? ((value - comparison) / Math.abs(comparison)) * 100 : undefined;

                // Trend data from time field if available
                let trendData: { value: number }[] | undefined;
                if (timeField) {
                    const sortedByTime = [...groupData].sort((a, b) => {
                        const timeA = new Date(a[timeField]).getTime();
                        const timeB = new Date(b[timeField]).getTime();
                        return timeA - timeB;
                    });
                    trendData = sortedByTime.slice(-10).map(row => ({ value: Number(row[valueField]) || 0 }));
                }

                items.push({
                    label: groupKey,
                    value,
                    comparison,
                    delta,
                    deltaPercent,
                    trendData,
                    groupValue: groupKey
                });
            });

            // Sort items
            items.sort((a, b) => {
                if (kpiConfig.sortBy === 'label') {
                    return kpiConfig.sortOrder === 'asc'
                        ? a.label.localeCompare(b.label)
                        : b.label.localeCompare(a.label);
                }
                return kpiConfig.sortOrder === 'asc' ? a.value - b.value : b.value - a.value;
            });

            return items;
        }

        // Single KPI card
        const value = data.reduce((sum, row) => sum + (Number(row[valueField]) || 0), 0);
        const comparison = comparisonField
            ? data.reduce((sum, row) => sum + (Number(row[comparisonField]) || 0), 0)
            : undefined;

        const delta = comparison !== undefined ? value - comparison : undefined;
        const deltaPercent = comparison && comparison !== 0 ? ((value - comparison) / Math.abs(comparison)) * 100 : undefined;

        // Trend data
        let trendData: { value: number }[] | undefined;
        if (timeField) {
            const sortedByTime = [...data].sort((a, b) => {
                const timeA = new Date(a[timeField]).getTime();
                const timeB = new Date(b[timeField]).getTime();
                return timeA - timeB;
            });
            trendData = sortedByTime.slice(-20).map(row => ({ value: Number(row[valueField]) || 0 }));
        }

        const label = config.title || 'KPI';

        return [{
            label,
            value,
            comparison,
            delta,
            deltaPercent,
            trendData,
            groupValue: null
        }];
    }, [data, config, kpiConfig]);

    if (kpiItems.length === 0) {
        return (
            <div className="w-full h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                    <Target className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">No data to display</p>
                    <p className="text-xs text-gray-500 mt-1">Map a Value Field to create a KPI Card</p>
                </div>
            </div>
        );
    }

    // Single card
    if (kpiItems.length === 1) {
        return (
            <div className="w-full h-full flex items-center justify-center p-4">
                <div className="w-full max-w-md">
                    <SingleKPICard
                        kpi={kpiItems[0]}
                        kpiConfig={kpiConfig}
                        onClick={kpiConfig.enableClickToFilter && onCardClick ? () => onCardClick(kpiItems[0].groupValue) : undefined}
                    />
                </div>
            </div>
        );
    }

    // Multiple cards grid
    return (
        <div className="w-full h-full p-4 overflow-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {kpiItems.map((kpi, idx) => (
                    <SingleKPICard
                        key={kpi.groupValue || idx}
                        kpi={kpi}
                        kpiConfig={kpiConfig}
                        onClick={kpiConfig.enableClickToFilter && onCardClick ? () => onCardClick(kpi.groupValue) : undefined}
                    />
                ))}
            </div>
        </div>
    );
};

export default KPICard;
